import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import axios from 'axios';

interface MathReportProps {
  tableData: any[];
  pharmacyNum: string | null;
  selectedMonth?: string;
  employeeName: string;
  username: string | null;
  disabled?: boolean;
}

const MathReport: React.FC<MathReportProps> = ({
  tableData,
  pharmacyNum,
  selectedMonth,
  username,
  employeeName,
  disabled = false
}) => {
  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [hasReports, setHasReports] = useState<boolean>(true);
  const [hasPos, setHasPos] = useState<boolean>(true);

  useEffect(() => {
    const fetchPharmacyNum = async () => {
      const username = localStorage.getItem('username');
      if (username) {
        try {
          const response = await axios.get('https://tbl.zerdegroup.kz/api/user-info', {
            params: { username }
          });
          if (response.data.success) {
            setHasReports(response.data.hasReports);
            setHasPos(response.data.pos === 1);
          }
        } catch (err) {
          console.error('Ошибка при получении номера аптеки:', err);
        }
      }
    };
    fetchPharmacyNum();
  }, []);

  const fetchReportData = async () => {
    if (!username || !selectedMonth) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/math-report-data', {
        params: {
          username,
          month: selectedMonth
        }
      });

      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        setError('Не удалось загрузить данные отчета');
      }
    } catch (err) {
      console.error('Ошибка при получении данных отчета:', err);
      setError('Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [username, selectedMonth]);

  const getMonthName = (): string => {
    const months = [
      'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
      'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'
    ];
    if (selectedMonth) {
      const monthIndex = parseInt(selectedMonth.split('-')[1]) - 1;
      return months[monthIndex];
    }
    return '';
  };

  const getYear = (): string => {
    if (selectedMonth) {
      return selectedMonth.split('-')[0];
    }
    return '';
  };

  const formatNumber = (value: any): string => {
    if (!value || value === error || parseFloat(value) === 0) return '';

    const num = parseFloat(value);
    const parts = num.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join(',');
  };

  // Helper function to safely parse numbers
  const parseValue = (value: any): number => {
    if (!value || typeof value !== 'string' && typeof value !== 'number') return 0;
    return typeof value === 'string' ?
      parseFloat(value.replace(/\s/g, '').replace(',', '.')) || 0 :
      parseFloat(value.toString()) || 0;
  };

  const generateReport = async () => {
    if (!reportData.length) return;

    try {
      const response = await fetch('/templates/material_report.docx');
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();

      // Calculate totals first
      const totals = tableData.reduce((acc, row) => ({
        totalCash: acc.totalCash + parseValue(row.cash),
        totalCashless: acc.totalCashless + parseValue(row.cashless)
      }), {
        totalCash: 0,
        totalCashless: 0
      });

      // Calculate sums before formatting
      const incomeValues = [
        parseValue(reportData[0]?.balanceOfStart),
        parseValue(reportData[0]?.arrivalFromStock),
        parseValue(reportData[0]?.beforeMarkup),
        parseValue(reportData[0]?.receiptOfGoods),
        parseValue(reportData[0]?.overage)
      ];

      const outcomeValues = [
        totals.totalCash,
        totals.totalCashless,
        parseValue(reportData[0]?.markdown),
        parseValue(reportData[0]?.returnToStock),
        parseValue(reportData[0]?.charity),
        parseValue(reportData[0]?.writeOff),
        parseValue(reportData[0]?.householdNeeds),
        parseValue(reportData[0]?.shortfall)
      ];

      const sumIncome = incomeValues.reduce((acc, val) => acc + val, 0);
      const sumOutcome = outcomeValues.reduce((acc, val) => acc + val, 0);
      const difference = sumIncome - sumOutcome;

      const rows = tableData.map((row, index) => ({
        num: index + 1,
        cash: formatNumber(row.cash),
        cashless: formatNumber(row.cashless),
        balanceOfStart: formatNumber(reportData[0]?.balanceOfStart),
        arrivalFromStock: formatNumber(reportData[0]?.arrivalFromStock),
        beforeMarkup: formatNumber(reportData[0]?.beforeMarkup),
        receiptOfGoods: formatNumber(reportData[0]?.receiptOfGoods),
        overage: formatNumber(reportData[0]?.overage),
        markdown: formatNumber(reportData[0]?.markdown),
        returnToStock: formatNumber(reportData[0]?.returnToStock),
        charity: formatNumber(reportData[0]?.charity),
        writeOff: formatNumber(reportData[0]?.writeOff),
        householdNeeds: formatNumber(reportData[0]?.householdNeeds),
        shortfall: formatNumber(reportData[0]?.shortfall)
      }));

      const data = {
        pharmacyNum: pharmacyNum ? pharmacyNum.toString() : '',
        currentMonth: getMonthName(),
        currentYear: getYear(),
        employeeName: employeeName,
        formValue: formatNumber(reportData[0]?.vat),
        rows,
        totalCash: formatNumber(totals.totalCash),
        totalCashless: formatNumber(totals.totalCashless),

        balanceOfStart: formatNumber(reportData[0]?.balanceOfStart),
        arrivalFromStock: formatNumber(reportData[0]?.arrivalFromStock),
        beforeMarkup: formatNumber(reportData[0]?.beforeMarkup),
        receiptOfGoods: formatNumber(reportData[0]?.receiptOfGoods),
        overage: formatNumber(reportData[0]?.overage),
        markdown: formatNumber(reportData[0]?.markdown),
        returnToStock: formatNumber(reportData[0]?.returnToStock),
        charity: formatNumber(reportData[0]?.charity),
        writeOff: formatNumber(reportData[0]?.writeOff),
        householdNeeds: formatNumber(reportData[0]?.householdNeeds),
        shortfall: formatNumber(reportData[0]?.shortfall),

        sumIncome: formatNumber(sumIncome),
        sumOutcome: formatNumber(sumOutcome),
        difference: difference === 0 ? '' : formatNumber(difference)
      };

      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true
      });

      doc.render(data);

      const out = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      const fileName = `Материальный отчет за ${selectedMonth} аптека №${pharmacyNum}.docx`;
      saveAs(out, fileName);

    } catch (error) {
      console.error('Ошибка при генерации отчета:', error);
      setError('Ошибка при создании отчета');
    }
  };

  return (
    <Button
      sx={{ background: 'brown' }}
      variant="contained"
      onClick={generateReport}
      disabled={!hasReports || loading || !reportData.length || !employeeName || !hasPos || disabled}
    >
      {loading ? 'Загрузка...' : 'Мат. отчет'}
    </Button>
  );
};

export default MathReport;